import { useState } from 'react';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shigo-ui/ui';

import { dashboardT } from '../../../../lib/messages';
import { formatLiverampCpm } from './TargetingForm.helpers';
import type { TargetingClient } from '../../api';
import type { TargetingFormValue } from '../../model';
import { InclusionToggle } from './InclusionToggle';
import { ManageLiverampSegmentsDialog } from './ManageLiverampSegmentsDialog';
import { ManagedSelectionSection } from './ManagedSelectionSection';
import { ManagedSelectionTable } from './ManagedSelectionTable';
import { managedSelectionTableClassNames } from './ManagedSelectionTable.styles';

export function LegacyLiverampTargetingSection({
  client,
  onChange,
  value,
}: {
  client: TargetingClient;
  onChange: (value: TargetingFormValue) => void;
  value: TargetingFormValue;
}) {
  const [manageOpen, setManageOpen] = useState(false);

  return (
    <>
      <ManagedSelectionSection
        title={dashboardT('targeting.liveramp.title')}
        selectedItems={value.liverampSegments}
        manageLabel={dashboardT('targeting.liveramp.manage')}
        clearLabel={dashboardT('targeting.liveramp.removeAll')}
        emptyText={dashboardT('targeting.liveramp.empty')}
        action={
          <InclusionToggle
            included={value.liverampSegmentsIncluded}
            onIncludedChange={(included) => {
              onChange({ ...value, liverampSegmentsIncluded: included });
            }}
          />
        }
        onManage={() => {
          setManageOpen(true);
        }}
        onClear={() => {
          onChange({ ...value, liverampSegments: [] });
        }}
      >
        <ManagedSelectionTable aria-label={dashboardT('targeting.liveramp.title')}>
          <TableHeader className={managedSelectionTableClassNames.header}>
            <TableRow className={managedSelectionTableClassNames.row}>
              <TableHead className={managedSelectionTableClassNames.head}>
                {dashboardT('targeting.liveramp.segmentName')}
              </TableHead>
              <TableHead className={managedSelectionTableClassNames.head}>
                {dashboardT('targeting.liveramp.providerName')}
              </TableHead>
              <TableHead className={`${managedSelectionTableClassNames.head} text-right`}>
                {dashboardT('targeting.liveramp.digitalCpm')}
              </TableHead>
              <TableHead className={`${managedSelectionTableClassNames.head} text-right`}>
                {dashboardT('targeting.liveramp.tvCpm')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {value.liverampSegments.map((segment) => (
              <TableRow key={segment.id} className={managedSelectionTableClassNames.row}>
                <TableCell className={managedSelectionTableClassNames.cell}>
                  {segment.segmentName || segment.id}
                </TableCell>
                <TableCell className={managedSelectionTableClassNames.cell}>
                  {segment.providerName}
                </TableCell>
                <TableCell className={`${managedSelectionTableClassNames.cell} text-right`}>
                  {formatLiverampCpm(segment.digitalCpm)}
                </TableCell>
                <TableCell className={`${managedSelectionTableClassNames.cell} text-right`}>
                  {formatLiverampCpm(segment.tvCpm)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ManagedSelectionTable>
      </ManagedSelectionSection>
      {manageOpen ? (
        <ManageLiverampSegmentsDialog
          open
          client={client}
          selectedItems={value.liverampSegments}
          onOpenChange={setManageOpen}
          onConfirm={(liverampSegments) => {
            onChange({ ...value, liverampSegments });
          }}
        />
      ) : null}
    </>
  );
}
